// Libraries
import { Flex, Skeleton, Text } from '@chakra-ui/react';
// Components
import { TextWithTooltip } from '@atoms/index';
import { useDebouncedState } from '@hooks/index';
import { CustomNumberInput, Dropdown } from '@molecules/index';
// Utils
import text from '@translations/en.json';
// Types
import { DropdownInputProps, OptionWithPrice } from './DropdownInput.types';

/**
 * A flexible input component that combines a dropdown and a number input.
 *
 * @component
 *
 * @template OptionType
 *
 * @param {DropdownInputProps<OptionType>} props - The props for the DropdownInput component.
 * @param {string} props.dropdownLabel - Label for the dropdown.
 * @param {OptionType} props.dropdownValue - Currently selected value in the dropdown.
 * @param {string} props.error - Current error message.
 * @param {string} props.inputLabel - Label for the number input.
 * @param {number} props.inputValue - Current value of the number input.
 * @param {boolean} [props.isDisabled=false] - Indicator if the components are disabled.
 * @param {boolean} [props.isLoading=false] - Indicator if the components are in a loading state.
 * @param {number} props.min - Minimum value for the number input.
 * @param {number} props.max - Maximum value for the number input.
 * @param {Function} props.onDropdownChange - Function to be called when the dropdown value changes.
 * @param {Function} props.onInputChange - Function to be called when the number input value changes.
 * @param {OptionType[]} props.options - Available options for the dropdown.
 * @param {string} props.tooltipText - Tooltip text to display alongside labels.
 * @returns {ReactElement} The DropdownInput component.
 */
export const DropdownInput = <OptionType extends OptionWithPrice>({
  dropdownLabel,
  dropdownValue,
  dropdownError,
  error,
  inputLabel,
  inputValue,
  quantityInputValue,
  onQuantityInputChange,
  quantityInputError,
  isDisabled = false,
  isLoading = false,
  max,
  min,
  onDropdownChange,
  onInputChange,
  options,
  tooltipText,
  doubleInputEnable = false,
  selectionLabel = text.no_options_selected,
}: DropdownInputProps<OptionType>) => {
  const [internalValue, setInternalValue] = useDebouncedState(inputValue, 10, onInputChange);
  const [quantityInternalValue, setQuantityInternalValue] = useDebouncedState(
    quantityInputValue,
    10,
    onQuantityInputChange
  );

  const pb = error ? 1 : 2;

  const handleDropdownChange = (selectedOptions: OptionType) => {
    onDropdownChange(selectedOptions);
  };

  return (
    <Flex gap={2} align='start' pt={2} pb={pb}>
      <Flex direction='column' gap={1} flexGrow={1}>
        <TextWithTooltip fontSize='s' color='grey.100' tooltipText={tooltipText} mt={2}>
          {dropdownLabel}
        </TextWithTooltip>
        <Skeleton flex='1' isLoaded={!isLoading}>
          <Dropdown
            name='fuelType'
            id='fuelType'
            options={options}
            selectionLabel={selectionLabel}
            value={dropdownValue}
            onChange={handleDropdownChange}
            isDisabled={isDisabled}
          />
          {!!dropdownError && (
            <Text h={3.5} mt={0.5} color='red'>
              {dropdownError}
            </Text>
          )}
        </Skeleton>
      </Flex>
      {doubleInputEnable && (
        <Flex direction='column' gap={1}>
          <TextWithTooltip fontSize='s' color='grey.100' tooltipText={tooltipText} mt={2}>
            {'Quantity'}
          </TextWithTooltip>
          <Skeleton isLoaded={!isLoading}>
            <CustomNumberInput
              isQuantity
              max={max}
              min={min}
              isDisabled={isDisabled}
              isInvalid={!!quantityInputError}
              value={quantityInternalValue}
              setValue={setQuantityInternalValue}
              width={21}
            />
            {!!quantityInputError && (
              <Text h={3.5} mt={0.5} color='red'>
                {quantityInputError}
              </Text>
            )}
          </Skeleton>
        </Flex>
      )}
      <Flex direction='column' gap={1}>
        <TextWithTooltip fontSize='s' color='grey.100' tooltipText={tooltipText} mt={2}>
          {inputLabel}
        </TextWithTooltip>
        <Skeleton isLoaded={!isLoading}>
          <CustomNumberInput
            max={max}
            min={min}
            isDisabled={isDisabled}
            isInvalid={!!error}
            value={internalValue}
            setValue={setInternalValue}
            width={21}
          />
          {!!error && (
            <Text h={3.5} mt={0.5} color='red'>
              {error}
            </Text>
          )}
        </Skeleton>
      </Flex>
    </Flex>
  );
};
